export const ASSESSMENTS = [
  { key: 'riasec', label: 'RIASEC' },
  { key: 'dass42', label: 'DASS-42' },
  { key: 'ryff', label: 'Ryff Scale' },
  { key: 'sdq', label: 'SDQ' },
];
export const ASSESSMENT_KEYS = ASSESSMENTS.map((item) => item.key);
export const ASSESSMENT_LABELS = Object.fromEntries(ASSESSMENTS.map((item) => [item.key, item.label]));
export const HAS_PRIORITY = { riasec: false, dass42: true, ryff: true, sdq: true };

export const STATUS_TABS = [
  ['to_review', 'To review'],
  ['released', 'Released'],
  ['withdrawn', 'Withdrawn'],
  ['all', 'All'],
];
export const STATUS_KEYS = STATUS_TABS.map(([key]) => key);

export const PRIORITY_LABELS = { critical: 'Critical', high: 'High', moderate: 'Moderate', low: 'Low' };
export const PRIORITY_FILTER_LABELS = {
  critical: 'Critical only',
  high: 'High and above',
  moderate: 'Moderate and above',
};
// The priority filters each assessment offers (SDQ and Ryff have no "critical" level).
export const PRIORITY_LEVELS = {
  dass42: ['critical', 'high', 'moderate'],
  ryff: ['high', 'moderate'],
  sdq: ['high', 'moderate'],
};
const PRIORITY_TONE = { critical: 4, high: 3, moderate: 2 };
export const priorityTone = (priority) => PRIORITY_TONE[priority];
export const needsIndividualReview = (row) => row.priority === 'critical' || row.priority === 'high';
// High and Critical results can be released together too, after a confirmation that names them.
export const canBulkRelease = (row) => row.status === 'to_review';

// Band rank (0 = least concern) to a tone class. Ranks come from the server.
const BAND_TONES = { dass42: [0, 1, 2, 3, 4], sdq: [0, 1, 3, 4], ryff: [0, 1, 3] };
export const bandTone = (type, rank) => (BAND_TONES[type] || [])[rank] ?? 0;

// Band names as stored and shown on the Results lists, to the same tones. "High"
// is good for Ryff well-being but a concern for SDQ, so tones are per assessment.
const BAND_LABEL_TONES = {
  dass42: { normal: 0, mild: 1, moderate: 2, severe: 3, 'extremely severe': 4 },
  sdq: {
    'close to average': 0, normal: 0,
    'slightly raised': 1, 'slightly lowered': 1, borderline: 1,
    high: 3, low: 3,
    'very high': 4, 'very low': 4, abnormal: 4,
  },
  ryff: { high: 0, moderate: 1, low: 3 },
};
export const bandToneFor = (type, label) => (BAND_LABEL_TONES[type] || {})[String(label || '').trim().toLowerCase()];

export const SORT_LABELS = {
  priority: 'Priority first',
  waiting: 'Waiting longest',
  newest: 'Newest first',
  name: 'Name A–Z',
};
export const defaultSort = (type) => (HAS_PRIORITY[type] ? 'priority' : 'waiting');
export const sortOptions = (type) => (HAS_PRIORITY[type] ? Object.keys(SORT_LABELS) : ['waiting', 'newest', 'name']);

export const LANGUAGES = [
  ['en', 'English'],
  ['fil', 'Filipino'],
  ['tl', 'Taglish'],
];
export const LANGUAGE_LABELS = Object.fromEntries(LANGUAGES);

export const PAGE_SIZES = [10, 25, 50];
export const DEFAULT_PAGE_SIZE = 25;
export const EXPLANATION_MAX_LENGTH = 1800;

export const RELEASE_CHOICES = [
  {
    value: 'after_release',
    title: 'After a counselor releases each result',
    description: 'Each result waits in To review until a counselor releases it.',
  },
  {
    value: 'immediate',
    title: 'Right after they finish',
    description: 'No review step. Results appear as soon as the assessment is complete.',
  },
  {
    value: 'hidden',
    title: 'Keep all results hidden',
    description: 'Nobody sees these results, including ones already released.',
  },
];

export const releaseChoice = (setting) => {
  if (!setting) return 'after_release';
  if (!setting.students_can_view) return 'hidden';
  return setting.require_review_before_release ? 'after_release' : 'immediate';
};

const dateFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const dateYearFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const timeFormat = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return (date.getFullYear() === new Date().getFullYear() ? dateFormat : dateYearFormat).format(date);
};

export const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return `${dateYearFormat.format(date)} at ${timeFormat.format(date)}`;
};

export const daysSince = (value) => {
  if (!value) return 0;
  return Math.round((startOfDay(new Date()) - startOfDay(new Date(value))) / 86400000);
};

export const relativeDay = (value) => {
  const days = daysSince(value);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

/** "Today at 2:15 PM" or "Yesterday at 9:40 AM"; older days keep relativeDay ("3 days ago"). */
export const completedWhen = (value) => {
  const date = toDate(value);
  if (!date) return '';
  const days = daysSince(date);
  if (days <= 0) return `Today at ${timeFormat.format(date)}`;
  if (days === 1) return `Yesterday at ${timeFormat.format(date)}`;
  return relativeDay(date);
};

/** "Just released" under a minute, then "Released 5 min ago", "Released 3 hours ago", "Released yesterday"… */
export const releasedAgo = (value) => {
  const date = toDate(value);
  if (!date) return 'Released';
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just released';
  if (minutes < 60) return `Released ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Released ${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  return `Released ${relativeDay(date).toLowerCase()}`;
};

export const toInputDate = (date) => {
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const initials = (name) => (name || 'User')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

export const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

/** "9 min", "1 hr 5 min", or "under 1 min" for the time spent answering a test. */
export const formatDuration = (seconds) => {
  const minutes = Math.round((Number(seconds) || 0) / 60);
  if (minutes < 1) return 'under 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
};

/**
 * How long a test took ({ seconds, sittings, days } from the server), or '' when it isn't known.
 * Short: "Took 18 min · 3 sittings". Long: "Took 16 min, in 2 sittings over 2 days".
 */
export const timeTakenText = (took, { short = false } = {}) => {
  if (!took || took.seconds == null) return '';
  const time = formatDuration(took.seconds);
  const sittings = Number(took.sittings) || 1;
  const days = Number(took.days) || 1;
  if (short) return sittings > 1 ? `Took ${time} · ${plural(sittings, 'sitting')}` : `Took ${time}`;
  if (sittings <= 1) return `Took ${time}`;
  return `Took ${time}, in ${plural(sittings, 'sitting')}${days > 1 ? ` over ${plural(days, 'day')}` : ''}`;
};

export const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

export const errorMessage = (error, fallback) => {
  if (error?.response?.status === 429) {
    return 'The AI draft limit for this hour has been reached. Try again later.';
  }
  return error?.response?.data?.error || fallback;
};
